import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import {
  DownloadOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { useEffect, useState } from "react";
import { FetchApiInterface } from "../../../../../interfaces/FetchApi.interface";
import { AxiosResponse } from "axios";
import { ResponseInterface } from "../../../../../interfaces/Response.interface";
import useFetch from "../../../../../hooks/useFetch";
import useMessageUser from "../../../../../hooks/useMessageUser";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import { getScopes } from "../../helperComms";
import PaginatedTable from "../../components/PaginatedTable";

const { Title } = Typography;

interface IBackup {
  id: number;
  useEmail: string;
  dpaName: string;
  fileName: string;
  createdAt: string;
  deviceInfo: string;
  uploadTimestamp: string;
}

function BackupList() {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const scopes = getScopes();

  const parseDeviceInfo = (deviceInfoString: string) => {
    try {
      const deviceInfo = JSON.parse(deviceInfoString);
      return {
        manufacturer: deviceInfo.manufacturer || t('notAvailable'),
        model: deviceInfo.model || t('notAvailable'),
        id: deviceInfo.id || t('notAvailable')
      };
    } catch (error) {
      return {
        manufacturer: t('notAvailable'),
        model: t('notAvailable'),
        id: t('notAvailable')
      };
    }
  };

  const initialColumns = [
    {
      title: t('backupId'),
      dataIndex: 'id',
      key: 'id',
      visible: true,
      fixed: 'left',
      width: 80,
    },
    {
      title: t('fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      visible: true,
      width: 200,
      render: (fileName: string) => {
        // Extract just the filename from the full path
        const name = fileName.split('/').pop() || fileName;
        return (
          <Tooltip title={fileName}>
            <span>{name}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('createdBy'),
      dataIndex: 'useEmail',
      key: 'useEmail',
      visible: true,
      width: 250,
    },
    {
      title: t('dpa'),
      dataIndex: 'dpaName',
      key: 'dpaName',
      visible: true,
      width: 120,
    },
    {
      title: t('device'),
      dataIndex: 'deviceInfo',
      key: 'deviceInfo',
      visible: true,
      width: 280,
      render: (deviceInfoString: string) => {
        const device = parseDeviceInfo(deviceInfoString);
        return (
          <div style={{ fontSize: '12px' }}>
            <div><strong>{t('brand')}:</strong> {device.manufacturer}</div>
            <div><strong>{t('model')}:</strong> {device.model}</div>
            <div><strong>{t('deviceId')}:</strong> {device.id}</div>
          </div>
        );
      },
    },
    {
      title: t('serverBackupDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      visible: true,
      width: 120,
      render: (item: string) => `${item ? moment(item).format('DD-MM-YYYY HH:mm') : ''}`,
    },
    {
      title: t('appBackupDate'),
      dataIndex: 'uploadTimestamp',
      key: 'uploadTimestamp',
      visible: true,
      width: 120,
      render: (item: string) => `${item ? moment(item).format('DD-MM-YYYY HH:mm') : ''}`,
    },
    {
      title: t('actions'),
      key: 'actions',
      visible: true,
      fixed: 'right',
      width: 150,
      render: (record: IBackup) => (
        <Space>
          <Tooltip title={t('downloadBackup')}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownloadBackup(record)}
              loading={downloadingId === record.id}
            >
              {t('downloadBackup')}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const [dataSource, setDataSource] = useState<Array<IBackup>>([]);
  const [columns] = useState<Array<any>>(initialColumns);
  const [searchText, setSearchText] = useState<string>('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const { fetchApi, downloadFile, loading } = useFetch();
  const { showMessage } = useMessageUser();

  useEffect(() => {
    getBackupList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBackupList = (page = 1, size = pagination.pageSize, search = searchText) => {
    const dataSend: FetchApiInterface = {
      url: `/comms/get-list-scuapp-backup?page=${page - 1}&size=${size}${search ? `&search=${search}` : ''}`,
      method: 'GET',
    };

    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          const dataResponse = data?.data;
          setDataSource(dataResponse?.content || []);
          setPagination({
            current: (dataResponse?.pageable?.pageNumber || 0) + 1,
            pageSize: size,
            total: dataResponse?.totalElements || 0,
          });
        } else {
          showMessage(data?.message || t('errorLoadingBackups'), 'error');
        }
      }
    ).catch((reason: any) => {
      showMessage(reason?.message || t('errorLoadingBackups'), 'error');
    });
  };

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    getBackupList(page, pageSize || pagination.pageSize);
  };

  const handleDownloadBackup = async (backup: IBackup) => {
    setDownloadingId(backup.id);

    try {
      const response = await downloadFile(`/comms/get-scuapp-backup/${backup.id}`, {}, 'get');
      debugger;
      if (response.status === 200) {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // Try to get filename from response headers or use default
        const filename = `backup_${backup.useEmail}_${backup.createdAt}.zip`;

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        showMessage(t('backupDownloaded'), 'success');
      }
    } catch (error: any) {
      showMessage(error.message || t('errorDownloadingBackup'), 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    getBackupList(1, pagination.pageSize, value);
  };

  const handleRefresh = () => {
    setSearchText('');
    setPagination(prev => ({ ...prev, current: 1 }));
    getBackupList(1, pagination.pageSize, '');
  };

  // Filter visible columns
  const visibleColumns = columns.filter(column => column.visible);

  return (
    <Row justify='center'>
      <Col span={24} className='p-1'>
        <Row justify='end' gutter={[20, 20]}>
          <Col span={24}>
            <Breadcrumb>
              <Breadcrumb.Item>SCUAPP</Breadcrumb.Item>
            </Breadcrumb>
            <Title level={3}>{t('titleBackup')}</Title>
          </Col>
          <Col span={24}>
            <Card className='border-radius'>
              <Row>
                <Col span={24}>
                  <Row justify='end'>
                    <Col>
                      <Space>
                        <Button
                          icon={<SyncOutlined />}
                          loading={loading}
                          onClick={handleRefresh}
                        />
                      </Space>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <PaginatedTable
                    dataSource={dataSource}
                    columns={visibleColumns}
                    rowKey="id"
                    scroll={{ x: 1500 }}
                    total={pagination.total}
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    onPaginationChange={handlePaginationChange}
                    onPageSizeChange={handlePaginationChange}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Col>
    </Row>

  );
}

export default BackupList;
